"use client";

import type { SiteSummary } from "@repo/gail-document";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { listSites } from "@/services/sites/client";

type OrgSitesStatus = "loading" | "ready" | "error";

interface OrgSitesContextValue {
  sites: SiteSummary[];
  status: OrgSitesStatus;
  refresh: () => Promise<void>;
}

const OrgSitesContext = createContext<OrgSitesContextValue | null>(null);

/** Without a provider (tests, other route groups) there are no sites, so nothing shows. */
const NO_SITES: OrgSitesContextValue = { sites: [], status: "ready", refresh: async () => {} };

/**
 * The current org's sites, fetched once per org so the nav, the Sites list and the dashboard
 * card share one request. A failed request reads as "no sites": the nav entry stays hidden
 * rather than linking to a page that cannot load. Every load (the org's first one or a refresh)
 * shows "loading" and takes a new generation; a response that arrives after a later load
 * started, or after the org changed, is ignored, so one org's sites never land in another org's
 * context. A signed-in user without an org has no sites, so that is "ready" and empty.
 */
export function OrgSitesProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated, user } = useAuthContext();
  const orgId = user?.orgId ?? null;
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [status, setStatus] = useState<OrgSitesStatus>("loading");

  const generation = useRef(0);

  const load = useCallback(async () => {
    generation.current += 1;
    const mine = generation.current;
    setStatus("loading");
    let items: SiteSummary[] | null = null;
    try {
      const response = await listSites();
      if (response.success && Array.isArray(response.data?.items)) items = response.data.items;
    } catch {
      // A rejected request (a session that expired mid-call) is a failed load like any other.
    }
    if (mine !== generation.current) return;
    setSites(items ?? []);
    setStatus(items ? "ready" : "error");
  }, []);

  const hasOrg = isAuthenticated && orgId !== null;

  useEffect(() => {
    if (!hasOrg) return;
    // The previous org's sites are never shown while the next org's load.
    setSites([]);
    void load();
    return () => {
      // Unmounting or switching org: any load still in flight is stale.
      generation.current += 1;
    };
  }, [hasOrg, orgId, load]);

  const value = useMemo<OrgSitesContextValue>(() => {
    if (isAuthenticated && !orgId) return NO_SITES;
    return { sites, status, refresh: load };
  }, [isAuthenticated, orgId, sites, status, load]);

  return <OrgSitesContext.Provider value={value}>{children}</OrgSitesContext.Provider>;
}

export function useOrgSites(): OrgSitesContextValue {
  return useContext(OrgSitesContext) ?? NO_SITES;
}
